import { useCallback, useEffect, useRef, useState } from 'react';
import { nativeCall, nativeState, onNative } from './nativeApp';

// useLiveRecording inside the Android app: the app records the screen with the
// teacher's face in a corner and the microphone, and uploads the video to the
// assessment itself (in chunks, in the background — leaving the page does not
// stop it). Same interface as the browser hook; `stream` is always null (the
// app shows the face bubble itself), `backlog` is 0.
const ACTIVE = ['starting', 'recording', 'paused', 'finishing'];

export default function useNativeLiveRecording({ en = false } = {}) {
  // a recording may already be running (started on another page)
  const [status, setStatusState] = useState(
    () => nativeState().status || 'idle'
  );
  const [elapsed, setElapsed] = useState(() => nativeState().elapsed || 0);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(null);
  const [progress, setProgress] = useState(null);
  const statusRef = useRef(status);
  const waiters = useRef([]);

  const setStatus = (s) => {
    statusRef.current = s;
    setStatusState(s);
  };

  useEffect(
    () =>
      onNative((d) => {
        if (typeof d.elapsed === 'number') setElapsed(d.elapsed);
        if (typeof d.progress === 'number') setProgress(d.progress);
        if (d.status && d.status !== statusRef.current) setStatus(d.status);
        if (d.status === 'saved') setSaved(d.asset || null);
        if (d.status === 'error')
          setError(
            d.error || (en ? 'The recording failed.' : 'Запись не получилась.')
          );
        if (
          ['recording', 'paused'].includes(d.status) === false &&
          d.status !== 'starting'
        ) {
          // finish() resolves once the recording has stopped (upload goes on)
          waiters.current.splice(0).forEach((w) => w(d.asset || null));
        }
      }),
    [en]
  );

  const start = useCallback(({ profileId, title = '' } = {}) => {
    if (!['idle', 'saved', 'error'].includes(statusRef.current)) return false;
    setError('');
    setSaved(null);
    setProgress(null);
    setElapsed(0);
    setStatus('starting');
    nativeCall('start', { profileId: profileId || null, title });
    return true;
  }, []);

  const pause = useCallback(() => nativeCall('pause'), []);
  const resume = useCallback(() => nativeCall('resume'), []);

  const finish = useCallback(() => {
    if (!['recording', 'paused'].includes(statusRef.current))
      return Promise.resolve(null);
    const p = new Promise((resolve) => waiters.current.push(resolve));
    nativeCall('finish');
    return p;
  }, []);

  const discard = useCallback(async () => {
    nativeCall('discard');
    setStatus('idle');
    setElapsed(0);
  }, []);

  return {
    status,
    active: ACTIVE.includes(status),
    elapsed,
    backlog: 0,
    error,
    stream: null,
    saved,
    progress,
    native: true,
    start,
    pause,
    resume,
    finish,
    discard,
  };
}
